/**
 * spaceRender.ts
 * Pure draw-list builder for the World 2 space shoot-em-up scene.
 *
 * Reads `spaceState` and returns a structured `SpaceDrawList` object with
 * separate `background`, `ship`, `projectiles`, `asteroids`, and `enemies`
 * fields (camera-relative rect descriptors). The Canvas2D renderer
 * (`webSpaceDraw.ts`) translates each field into draw primitives in the
 * order the type lays them out.
 *
 * Why a builder rather than direct draw calls:
 *   - The output is a plain object of arrays, so it is trivially
 *     unit-testable without mocking HTMLCanvasElement.
 *
 * No game-state mutation — read-only from `spaceState`.
 *
 * Per-entity-family rect builders (`buildBossRect`, `buildMineRects`,
 * etc.) live in {@link ./spaceDrawBuilders} so this entry-point file
 * stays under the AGENTS.md 300-line cap.
 *
 * Phase 4 — World 2: Starfield Drift (greybox renderer pass).
 *
 * @module spaceRender
 */

import { spaceState } from '../engine/spaceState';
import {
  SHIP_WIDTH,
  SHIP_HEIGHT,
  SHIP_IFRAME_BLINK_RATE_HZ,
} from '../../constants/spaceConstants';
import {
  SPACE_BG_COLOR,
  SPACE_BG_PARALLAX_FACTOR,
  SPACE_SHIP_COLOR,
  SPACE_SHIP_HIT_FLASH_COLOR,
  SPACE_PROJECTILE_COLOR,
  SPACE_MISSILE_COLOR,
  SPACE_ASTEROID_COLORS,
} from '../../constants/rendering';
import {
  SPACE_ENEMY_COLOR_BY_KIND,
  buildBossRect,
  buildBossShieldRects,
  buildBossProjectileRects,
  buildBossMissileRects,
  buildFighterDroneLaserRects,
  buildMineRects,
  buildShardRects,
  buildFogRect,
} from './spaceDrawBuilders';
import { buildLootRects } from './spaceLootDrawBuilders';
import {
  buildClusterBombRects,
  buildBombShrapnelRects,
} from './spaceBomberOrdnanceBuilders';
import { buildExplosionRects } from './spaceExplosionRects';
import { buildSpaceBgDecorations } from './spaceBgDecorations';
import type { SpaceRect, SpaceDrawList } from './spaceRenderTypes';

// Re-export the shared types so callers that still import from
// `./spaceRender` keep working without an import path change.
export type { SpaceRect, SpaceDrawList };

/**
 * Build a camera-relative draw list for the current `spaceState`.
 *
 * Returns an empty draw list (background only, no entities) when the space
 * scene is not currently running so the renderer can safely call this in any
 * frame without a `spaceState.isRunning` guard of its own.
 *
 * @returns Structured draw list — see {@link SpaceDrawList}.
 */
export function buildSpaceDrawList(): SpaceDrawList {
  if (!spaceState.isRunning) {
    // DECISION: Always allocate a fresh empty list rather than returning a
    // shared module-level constant — the field arrays are mutable, so a stray
    // `.push()` on the idle result would poison every later idle draw. The
    // idle path runs at most once per frame so the alloc cost is negligible.
    return {
      background: { color: SPACE_BG_COLOR, parallaxOffset: 0 },
      bgDecorations: [],
      ship: null,
      projectiles: [],
      missiles: [],
      asteroids: [],
      enemies: [],
      boss: null,
      bossShield: [],
      bossProjectiles: [],
      bossMissiles: [],
      fighterLasers: [],
      clusterBombs: [],
      bombShrapnel: [],
      mines: [],
      shards: [],
      loot: [],
      fog: null,
      explosions: [],
    };
  }

  const cameraX = spaceState.cameraX;

  let ship: SpaceRect | null = null;
  if (spaceState.ship !== null) {
    const s = spaceState.ship;
    // DECISION: Blink parity is derived from the surviving iframe
    // budget rather than a wall clock. That keeps the strobe
    // deterministic in tests (no `Date.now` / `performance.now`) and
    // pauses cleanly with the game loop. Visual state is chosen by
    // `floor(iframesRemaining * rate * 2) % 2 === 0`. Whether a fresh
    // hit lands on the flashed half depends on the parity of
    // `floor(SHIP_INVULN_DURATION_S * rate * 2)` — with the current
    // tuning (1.5 * 12 * 2 = 36, even) it does, and the
    // shipIframe-blink tests pin that contract.
    const blinking = s.iframesRemaining > 0;
    const flashed =
      blinking &&
      Math.floor(s.iframesRemaining * SHIP_IFRAME_BLINK_RATE_HZ * 2) % 2 === 0;
    ship = {
      x: s.x - cameraX,
      y: s.y,
      width: SHIP_WIDTH,
      height: SHIP_HEIGHT,
      color: flashed ? SPACE_SHIP_HIT_FLASH_COLOR : SPACE_SHIP_COLOR,
      key: 'ship',
    };
  }

  const projectiles: SpaceRect[] = [];
  for (const p of spaceState.projectiles) {
    if (p.dead) continue;
    projectiles.push({
      x: p.x - cameraX,
      y: p.y,
      width: p.width,
      height: p.height,
      color: SPACE_PROJECTILE_COLOR,
      // DECISION: `spawnId` is monotonic per pooled instance so React keys
      // never collide even as the pool recycles instances.
      key: `p${p.spawnId}`,
    });
  }

  const missiles: SpaceRect[] = [];
  for (const m of spaceState.missiles) {
    if (m.dead) continue;
    missiles.push({
      x: m.x - cameraX,
      y: m.y,
      width: m.width,
      height: m.height,
      color: SPACE_MISSILE_COLOR,
      // Stable React/debug key keyed on the missile pool's monotonic
      // `spawnId`, matching the projectile builder above.
      key: `m${m.spawnId}`,
    });
  }

  const asteroids: SpaceRect[] = [];
  for (const a of spaceState.asteroids) {
    if (a.dead) continue;
    asteroids.push({
      x: a.x - cameraX,
      y: a.y,
      width: a.width,
      height: a.height,
      color: SPACE_ASTEROID_COLORS[a.size],
      // DECISION: Sprite id mirrors the size enum so the renderer can
      // pick the matching asteroid PNG with a single `Record` lookup
      // instead of branching per size. Builder stays asset-agnostic.
      spriteId: `asteroid-${a.size}`,
      key: `a${a.spawnId}`,
    });
  }

  const enemies: SpaceRect[] = [];
  for (const e of spaceState.enemies) {
    if (e.dead) continue;
    enemies.push({
      x: e.x - cameraX,
      y: e.y,
      width: e.width,
      height: e.height,
      // DECISION: Palette is keyed off `e.kind` via an exhaustive
      // `Record<SpaceEnemyKind, string>` so the renderer never imports
      // concrete entity classes and a missing colour for a new kind is a
      // compile error rather than a silent fighter-palette fallback.
      color: SPACE_ENEMY_COLOR_BY_KIND[e.kind],
      // DECISION: Sprite id keyed off `e.kind` so a new kind is a
      // compile error here too (the renderer's id→sprite Map must
      // grow alongside `SpaceEnemyKind`). Builder stays asset-agnostic.
      spriteId: `enemy-${e.kind}`,
      key: `e${e.spawnId}`,
    });
  }

  // DECISION: Explosion rect build lives in
  // `spaceExplosionRects.buildExplosionRects` so both this entry-point
  // and `spaceDrawBuilders` stay under the AGENTS.md 300-line cap. The
  // dedicated module owns the explosion-frame palette and the pool
  // iteration.

  return {
    background: {
      color: SPACE_BG_COLOR,
      parallaxOffset: cameraX * SPACE_BG_PARALLAX_FACTOR,
    },
    bgDecorations: buildSpaceBgDecorations(),
    ship,
    projectiles,
    missiles,
    asteroids,
    enemies,
    boss: buildBossRect(cameraX),
    bossShield: buildBossShieldRects(cameraX),
    bossProjectiles: buildBossProjectileRects(cameraX),
    bossMissiles: buildBossMissileRects(cameraX),
    fighterLasers: buildFighterDroneLaserRects(cameraX),
    clusterBombs: buildClusterBombRects(cameraX),
    bombShrapnel: buildBombShrapnelRects(cameraX),
    mines: buildMineRects(cameraX),
    shards: buildShardRects(cameraX),
    loot: buildLootRects(cameraX),
    fog: buildFogRect(),
    explosions: buildExplosionRects(cameraX),
  };
}
